import { useNavigate } from "react-router-dom";
import { MoreVertical } from "lucide-react";
import { useState, useEffect, useContext, useRef } from "react";
import AuthContext from "../../store/auth-context";
import { GET, PUT, DEL } from "../../service/CRUDService";
import Toast from "../../common/Toast/Toast";
import CfLoader from "../../common/CfLoader/CfLoader";
import EntityConstants from "../../configs/EntityConstants";
import PlusCircle from "../../resources/icons/plus-circle.svg";
import CreateProgrammeModal from "../../common/CreateProgrammeModal/CreateProgrammeModal";
import useNavigationStore from "../../store/navigationStore";
import DraftsRevokeModal from "../../common/DraftsRevokeModal/DraftsRevokeModal";
import useSearchDebounce from "../../hooks/useSearchDebounce";
import { useTranslation } from "react-i18next";
import { pageRootTestIds } from "../../testing/pageRootTestIds";
import CalculateCardsPerRow from "../../service/CalculateCardsPerRow";

export default function ProgrammePage() {
  const authContext = useContext(AuthContext);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const setProgrammeInStore = useNavigationStore((state) => state.setProgramme);

  const [searchTerm, setSearchTerm] = useState("");
  const [programmes, setProgrammes] = useState([]);
  const [previous, setPrevious] = useState(null);
  const [next, setNext] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [batchName, setBatchName] = useState("");
  const [showDropdown, setShowDropdown] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [currentProgramme, setCurrentProgramme] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(null);
  const [cardWidth] = useState("22rem");
  const [gap] = useState("1rem");
  const containerRef = useRef(null);
  const [cardsPerRow, setCardsPerRow] = useState(0);
  const rowsPerPage = 2;

  useEffect(() => {
    const updateCardsPerRow = () => {
      if (containerRef.current) {
        const calculated = CalculateCardsPerRow(containerRef, cardWidth, gap);
        setCardsPerRow(calculated < 1 ? 1 : calculated);
      }
    };
    updateCardsPerRow();
    window.addEventListener("resize", updateCardsPerRow);
    return () => window.removeEventListener("resize", updateCardsPerRow);
  }, [cardWidth, gap]);

  const fetchProgrammes = (limit, offset, search = "") => {
    setIsLoading(true);
    const queryParams = new URLSearchParams({
      limit: String(limit),
      offset: String(offset),
    });
    if (search.trim()) {
      queryParams.set("search", search.trim());
    }
    const endpoint = `${EntityConstants.GET_PROGRAMME_DETAILS}?${queryParams.toString()}`;
    GET(endpoint)
      .then((response) => response.json())
      .then((data) => {
        if (data.error) {
          Toast({
            type: "error",
            title: t("workspace:error"),
            message: data.error,
          });
        } else {
          const activeProgrammes = data.data.results.filter(
            (programme) => programme.is_active,
          );
          setProgrammes(activeProgrammes);
          setPrevious(data.data.previous);
          setNext(data.data.next);
          authContext.ProgrammeOffsetHandler(offset);
        }
      })
      .catch((error) => {
        console.error(error);
        Toast({
          type: "error",
          title: t("workspace:error"),
          message: t("workspace:failed_fetch_programmes"),
        });
      })
      .finally(() => {
        setIsLoading(false);
        setIsSearching(false);
      });
  };

  const performSearchAPI = async (searchTerm, signal) => {
    try {
      const pageSize = cardsPerRow > 0 ? cardsPerRow * rowsPerPage : 8;
      const queryParams = new URLSearchParams({
        limit: String(pageSize),
        offset: "0",
        search: searchTerm.trim(),
      });
      const endpoint = `${EntityConstants.GET_PROGRAMME_DETAILS}?${queryParams.toString()}`;
      const response = await GET(endpoint, { signal });
      const data = await response.json();

      if (data.error) {
        Toast({
          type: "error",
          title: t("workspace:error"),
          message: data.error,
        });
        setProgrammes([]);
      } else {
        const activeProgrammes = data.data.results.filter(
          (programme) => programme.is_active,
        );
        setProgrammes(activeProgrammes);
        setPrevious(data.data.previous);
        setNext(data.data.next);
        authContext.ProgrammeOffsetHandler(0);
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error(error);
        Toast({
          type: "error",
          title: t("workspace:error"),
          message: t("workspace:failed_fetch_programmes"),
        });
        setProgrammes([]);
      }
    }
  };

  const handlePreviousPage = () => {
    const pageSize = cardsPerRow > 0 ? cardsPerRow * rowsPerPage : 8;
    const newOffset = Math.max(
      0,
      (authContext.programmeOffset || 0) - pageSize,
    );
    if (searchTerm.trim()) {
      fetchProgrammes(pageSize, newOffset, searchTerm);
      return;
    }

    authContext.ProgrammeOffsetHandler(newOffset);
  };

  const handleNextPage = () => {
    const pageSize = cardsPerRow > 0 ? cardsPerRow * rowsPerPage : 8;
    const newOffset = (authContext.programmeOffset || 0) + pageSize;
    if (searchTerm.trim()) {
      fetchProgrammes(pageSize, newOffset, searchTerm);
      return;
    }

    authContext.ProgrammeOffsetHandler(newOffset);
  };

  const { handleSearch, cleanup: cleanupSearch } = useSearchDebounce(
    performSearchAPI,
    500,
  );

  const handleProgrammeCreated = () => {
    const pageSize = cardsPerRow > 0 ? cardsPerRow * rowsPerPage : 8;
    fetchProgrammes(pageSize, authContext.programmeOffset || 0);
  };

  const refreshProgrammesAfterMutation = () => {
    const pageSize = cardsPerRow > 0 ? cardsPerRow * rowsPerPage : 8;
    if (searchTerm) {
      setIsSearching(true);
      performSearchAPI(searchTerm).finally(() => setIsSearching(false));
      return;
    }

    fetchProgrammes(pageSize, authContext.programmeOffset || 0);
  };

  const handleEditProgramme = (programme) => {
    setCurrentProgramme(programme);
    setEditModalOpen(true);
    setShowDropdown(null);
  };

  const deleteProgramme = async (programmeCode) => {
    setIsDeleting(true);

    const payload = {
      programme_code: programmeCode,
    };

    try {
      const response = await DEL(
        `${EntityConstants.GET_PROGRAMME_DETAILS}?programme_id=${programmeCode}`,
        payload,
      );
      const data = await response.json();

      if (response.ok) {
        Toast({
          type: "success",
          title: t("workspace:success"),
          message: t("workspace:success_delete_programme"),
        });
        if (searchTerm) {
          refreshProgrammesAfterMutation();
          return;
        }
        const currentOffset = authContext.programmeOffset || 0;
        const pageSize = cardsPerRow > 0 ? cardsPerRow * rowsPerPage : 8;

        if (programmes.length === 1 && currentOffset > 0) {
          const newOffset = Math.max(0, currentOffset - pageSize);
          fetchProgrammes(pageSize, newOffset, searchTerm);
        } else {
          fetchProgrammes(pageSize, currentOffset, searchTerm);
        }
      } else {
        Toast({
          type: "error",
          title: t("workspace:error"),
          message: data.error || t("workspace:failed_delete_programme"),
        });
      }
    } catch (error) {
      console.error(error);
      Toast({
        type: "error",
        title: t("workspace:error"),
        message: t("workspace:failed_delete_programme"),
      });
    } finally {
      setIsDeleting(false);
      setIsDeleteModalOpen(null);
      setCurrentProgramme(null);
    }
  };

  const handleDeleteProgramme = (programme) => {
    setCurrentProgramme(programme);
    setIsDeleteModalOpen(programme.programme_code);
    setShowDropdown(null);
  };

  const confirmDeleteProgramme = () => {
    if (currentProgramme) {
      deleteProgramme(currentProgramme.programme_code);
    }
  };

  const handleProgrammeUpdate = (
    updatedName,
    updatedDescription,
    updatedDisplayName,
    learningOutcomes,
    skills,
    courseUrl,
  ) => {
    const payload = {
      programme_code: currentProgramme.programme_code,
      programme_name: updatedName,
      description: updatedDescription,
      programme_display_name: updatedDisplayName,
      learning_outcomes: learningOutcomes || [],
      skills: skills || [],
      course_url: courseUrl || "",
    };

    PUT(EntityConstants.GET_PROGRAMME_DETAILS, payload)
      .then((response) => response.json())
      .then((data) => {
        if (data.error) {
          Toast({
            type: "error",
            title: t("workspace:error"),
            message: data.error,
          });
        } else {
          Toast({
            type: "success",
            title: t("workspace:success"),
            message: t("workspace:success_update_programme"),
          });
          refreshProgrammesAfterMutation();
        }
      })
      .catch((error) => {
        console.error(error);
        Toast({
          type: "error",
          title: t("workspace:error"),
          message: t("workspace:failed_update_programme"),
        });
      });
  };

  const fetchBatchName = () => {
    GET(EntityConstants.GET_BATCH_NAME)
      .then((response) => response.json())
      .then((data) => {
        if (data.batch_display_name) {
          setBatchName(data.batch_display_name);
        }
      })
      .catch((error) => {
        console.error("Error fetching batch name:", error);
      });
  };

  useEffect(() => {
    fetchBatchName();
  }, []);

  useEffect(() => {
    if (authContext.programmeOffset !== null) {
      localStorage.setItem("programmeOffset", authContext.programmeOffset);
    }
  }, [authContext.programmeOffset]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(null);
      }
    };

    if (showDropdown !== null) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDropdown]);

  const handleProgrammeLinkClick = (programme) => {
    setProgrammeInStore(programme);
    navigate(`/issuer/workspace/batches/${programme.programme_code}`);
  };

  const handleSearchInputChange = (e) => {
    const value = e.target.value ? e.target.value : "";
    setSearchTerm(value);
    authContext.ProgrammeOffsetHandler(0);

    if (value.length > 0) {
      setIsSearching(true);
      handleSearch(value, {
        onStart: () => setIsSearching(true),
        onComplete: () => setIsSearching(false),
        onError: () => {
          setProgrammes([]);
          setIsSearching(false);
        },
        minChars: 1,
      });
    } else {
      cleanupSearch();
      setIsSearching(false);
      const pageSize = cardsPerRow > 0 ? cardsPerRow * rowsPerPage : 8;
      fetchProgrammes(pageSize, 0);
    }
  };

  useEffect(() => {
    if (!searchTerm && cardsPerRow > 0) {
      fetchProgrammes(
        cardsPerRow * rowsPerPage,
        authContext.programmeOffset || 0,
      );
    }
  }, [authContext.programmeOffset, cardsPerRow]);

  return (
    <div
      className="w-full mt-20 bg-cfSecondary min-h-screen"
      id={pageRootTestIds.programme}
    >
      <div ref={containerRef}>
        <div
          className={`w-full bg-cfSearchContainer flex justify-center py-[0.7rem] px-4 gap-2`}
        >
          <div
            className={`flex flex-col lg:flex-row items-start lg:items-center justify-between w-full gap-2 lg:gap-4`}
          >
            <p className="text-cfHeadingText font-semibold self-start my-auto">
              {t("workspace:workspace")}
            </p>

            {isModalOpen && (
              <CreateProgrammeModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onProgrammeCreated={handleProgrammeCreated}
              />
            )}
            <div className="flex flex-col lg:flex-row items-stretch lg:items-center lg:justify-end gap-2 lg:gap-4 w-full lg:w-auto">
              <button
                onClick={() => setIsModalOpen(true)}
                id="programme-page-create-programme"
                className="h-[2.75rem] w-full lg:w-auto lg:min-w-[12.5rem] px-2 gap-2 bg-cfPrimaryButton text-white rounded-[8px] flex items-center justify-center hover:bg-cfPrimaryDark transition duration-200 border border-gray-800"
              >
                <img src={PlusCircle} className="h-4 w-4" alt="" />
                <span className="font-medium">
                  {t("workspace:create_programme")}
                </span>
              </button>
              <div className="bg-cfSecondary p-2 rounded-lg w-full lg:w-[25rem]">
                <input
                  type="text"
                  name="search"
                  id="programme-page-input-search"
                  placeholder={t("workspace:search_programme")}
                  className="outline-none border-none p-0 placeholder:text-sm w-full"
                  style={{ fontSize: "0.875rem", lineHeight: "1.25rem" }}
                  onChange={handleSearchInputChange}
                  value={searchTerm}
                />
              </div>
            </div>
          </div>
        </div>

        {isLoading || isSearching ? (
          <div className="col-span-full w-full flex justify-center items-center py-4">
            <CfLoader />
          </div>
        ) : programmes.length > 0 ? (
          <>
            <div
              className="px-4 mt-4"
              style={{
                display: "grid",
                gridTemplateColumns:
                  cardsPerRow > 0
                    ? `repeat(${cardsPerRow}, minmax(0, 1fr))`
                    : "repeat(1, minmax(0, 1fr))",
                gap: gap,
              }}
            >
              {programmes
                .filter((programme) => programme.is_active)
                .map((programme) => (
                  <div
                    key={programme.programme_code}
                    id={`programme-page-card-${programme.programme_code}`}
                    onClick={() => handleProgrammeLinkClick(programme)}
                    onKeyDown={(e) => {
                      if (e.target !== e.currentTarget) return;
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleProgrammeLinkClick(programme);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    className="border rounded-xl overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer bg-white h-full flex flex-col"
                  >
                    <div className="h-[6rem] relative flex items-start justify-center top-3">
                      <div className="absolute inset-0 bg-gradient-to-t border-b mx-3"></div>
                      <div className="absolute left-[1rem] right-[1rem]">
                        <div className="group flex justify-between items-center relative min-w-0">
                          <h3
                            id={`programme-page-title-${programme.programme_code}`}
                            className="font-semibold text-cfPrimaryButton transition-colors text-lg line-clamp-3 pr-8 min-w-0 max-w-full break-words"
                            title={programme.programme_name}
                          >
                            {programme.programme_name}
                          </h3>
                          <div className="absolute right-0 top-0 h-full flex items-start">
                            <div className="relative">
                              <button
                                type="button"
                                className="mt-2 rounded-sm text-gray-700 hover:text-cfPrimaryButton transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimaryButton focus-visible:ring-offset-2"
                                id={`programme-page-icon-more-${programme.programme_code}`}
                                aria-label={t("workspace:actions_for", {
                                  name: programme.programme_name,
                                })}
                                aria-haspopup="menu"
                                aria-expanded={
                                  showDropdown === programme.programme_code
                                }
                                aria-controls={`programme-page-menu-${programme.programme_code}`}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setShowDropdown(
                                    showDropdown === programme.programme_code
                                      ? null
                                      : programme.programme_code,
                                  );
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === "Escape") {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setShowDropdown(null);
                                  }
                                }}
                              >
                                <MoreVertical
                                  className="h-[1.1rem] w-[1.1rem]"
                                  aria-hidden="true"
                                />
                              </button>
                              {showDropdown === programme.programme_code && (
                                <div
                                  ref={dropdownRef}
                                  id={`programme-page-menu-${programme.programme_code}`}
                                  role="menu"
                                  tabIndex={-1}
                                  aria-labelledby={`programme-page-icon-more-${programme.programme_code}`}
                                  className="absolute right-0 mt-2 w-28 bg-white rounded-md shadow-lg z-10 border"
                                  onKeyDown={(e) => {
                                    if (e.key === "Escape") {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      setShowDropdown(null);
                                    }
                                  }}
                                >
                                  <div>
                                    <button
                                      id={`programme-page-edit-${programme.programme_code}`}
                                      role="menuitem"
                                      className="block w-full text-left px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimaryButton focus-visible:ring-inset"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleEditProgramme(programme);
                                      }}
                                    >
                                      {t("workspace:edit")}
                                    </button>
                                    <button
                                      id={`programme-page-delete-${programme.programme_code}`}
                                      role="menuitem"
                                      className="block w-full text-left border-t border-gray-200 px-4 py-2 text-sm font-medium text-red-600 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimaryButton focus-visible:ring-inset disabled:opacity-50 disabled:cursor-not-allowed"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleDeleteProgramme(programme);
                                      }}
                                      disabled={isDeleting}
                                    >
                                      {isDeleting
                                        ? t("workspace:deleting")
                                        : t("workspace:delete")}
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="p-3 flex-1 flex flex-col">
                      <div>
                        <p
                          id={`programme-page-description-${programme.programme_code}`}
                          className="text-sm text-gray-500 mt-[0.5rem] mb-[1rem] truncate"
                        >
                          {programme.description}
                        </p>
                      </div>
                      <div className="mt-auto flex flex-wrap gap-5">
                        <div className="bg-gray-100 text-gray-800 px-3 py-1 rounded-full text-sm font-medium">
                          {programme.number_of_batches}{" "}
                          {batchName ? batchName : t("workspace:batches")}
                        </div>
                        <div className="bg-gray-100 text-gray-800 px-3 py-1 rounded-full text-sm font-medium">
                          {programme.number_of_templates}{" "}
                          {t("workspace:templates")}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
            </div>

            <div className="flex items-center justify-between mt-6 w-full px-4 pb-4">
              <button
                onClick={handlePreviousPage}
                id="programme-page-pagination-previous"
                className="flex items-center justify-center px-5 py-2 w-36 text-sm active:text-gray-700 capitalize transition-colors duration-200 bg-cfSecondary border rounded-md gap-x-2 hover:bg-gray-100 disabled:text-gray-400"
                disabled={previous === null}
              >
                <span className="flex items-center justify-center gap-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="w-5 h-5 rtl:-scale-x-100"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6.75 15.75L3 12m0 0l3.75-3.75M3 12h18"
                    />
                  </svg>
                  <span>{t("workspace:previous")}</span>
                </span>
              </button>

              <button
                onClick={handleNextPage}
                id="programme-page-pagination-next"
                className="flex items-center justify-center px-5 py-2 w-36 text-sm active:text-gray-700 capitalize transition-colors duration-200 bg-cfSecondary border rounded-md gap-x-2 hover:bg-gray-100 disabled:text-gray-400"
                disabled={next === null}
              >
                <span className="flex items-center justify-center gap-2">
                  <span>{t("workspace:next")}</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="w-5 h-5 rtl:-scale-x-100"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M17.25 8.25L21 12m0 0l-3.75 3.75M21 12H3"
                    />
                  </svg>
                </span>
              </button>
            </div>
          </>
        ) : (
          <div className="mt-6 w-full flex justify-center">
            <p
              id="programme-page-empty-message"
              className="text-lg font-semibold text-cfSignUpText"
            >
              {t("workspace:no_programme_found")}
            </p>
          </div>
        )}
      </div>

      {editModalOpen && currentProgramme && (
        <CreateProgrammeModal
          isOpen={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          onProgrammeCreated={() => {
            fetchProgrammes(
              cardsPerRow > 0 ? cardsPerRow * rowsPerPage : 8,
              authContext.programmeOffset || 0,
            );
            setEditModalOpen(false);
          }}
          isEditMode={true}
          initialName={currentProgramme.programme_name}
          initialDescription={currentProgramme.description}
          initialDisplayName={currentProgramme.programme_display_name}
          initialLearningOutcomes={currentProgramme.learning_outcomes || []}
          initialSkills={currentProgramme.skills || []}
          initialCourseUrl={currentProgramme.course_url || ""}
          onUpdate={(
            updatedName,
            updatedDescription,
            updatedDisplayName,
            learningOutcomes,
            skills,
            courseUrl,
          ) => {
            handleProgrammeUpdate(
              updatedName,
              updatedDescription,
              updatedDisplayName,
              learningOutcomes,
              skills,
              courseUrl,
            );
          }}
        />
      )}
      <DraftsRevokeModal
        isOpen={isDeleteModalOpen !== null}
        onClose={() => {
          setIsDeleteModalOpen(null);
          setCurrentProgramme(null);
        }}
        onConfirm={confirmDeleteProgramme}
        title={t("workspace:delete_programme")}
        description={`${t("workspace:delete_programme_description_1")} "${
          currentProgramme?.programme_name
        }"${t("workspace:delete_programme_description_2")}`}
        confirmButtonText={t("workspace:delete")}
        cancelButtonText={t("workspace:cancel")}
      />
    </div>
  );
}
